import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth, useTournament, useMatchPlay } from '@shootout-comps/core';
import type { AudioAdapter } from '@shootout-comps/core';
import { useMatchSocket, useConnectionStatus } from '../adapters/webSocket';
import { useAudio } from '../hooks/useAudio';
import { useLang } from '@shootout-comps/i18n';
import MatchScoringSurface from '../components/MatchScoringSurface';

interface Props { matchId: string; onBack: () => void; }

export default function MatchPlayPage({ matchId, onBack }: Props) {
  const { getAccessToken, isSuperuser } = useAuth();
  const { fetchMatchTournamentFormat, pauseAllClocks, resumeAllClocks } = useTournament();
  const { t } = useLang();

  // Resolved independently from this match's own tournament — reading a globally "open"
  // tournament here would be wrong once tournaments can run concurrently (this match might
  // not belong to whichever one happens to be open elsewhere in the app).
  const [matchTournament, setMatchTournament] = useState<{ _id: string; format: string } | null>(null);
  useEffect(() => { fetchMatchTournamentFormat(matchId).then(setMatchTournament).catch(() => null); }, [matchId]); // eslint-disable-line
  const tournamentFmt = matchTournament?.format ?? 'shootout';
  const isStandard    = tournamentFmt === 'standard';

  // ── Audio — wrapped so the mute toggle (a web-only feature) gates the four
  // synthesized clock cues without affecting silenceAll/unsilence, which must always
  // function for clock-pause/resume synchronization regardless of the mute preference.
  const rawAudio = useAudio();
  const [soundsMuted, setSoundsMuted] = useState(false);
  const soundsMutedRef = useRef(false);
  useEffect(() => { soundsMutedRef.current = soundsMuted; }, [soundsMuted]);
  const audio = useMemo<AudioAdapter>(() => ({
    playBeep: (...args) => { if (!soundsMutedRef.current) rawAudio.playBeep(...args); },
    playCountdownBeeps: (...args) => { if (!soundsMutedRef.current) rawAudio.playCountdownBeeps(...args); },
    playBuzzer: () => { if (!soundsMutedRef.current) rawAudio.playBuzzer(); },
    playMatchExpiry: () => { if (!soundsMutedRef.current) rawAudio.playMatchExpiry(); },
    speakAnnouncement: (text: string) => { if (!soundsMutedRef.current) rawAudio.speakAnnouncement(text); },
    silenceAll: (cancelSpeech?: boolean) => rawAudio.silenceAll(cancelSpeech),
    unsilence: () => rawAudio.unsilence(),
  }), [rawAudio]);

  const handleToggleMute = (muted: boolean) => {
    setSoundsMuted(muted);
    if (muted) audio.silenceAll(true);
  };

  // ── Web-only UI state (not part of the shared hook) ─────────────────────────
  const [allPaused, setAllPaused]     = useState(false);
  const [pausing, setPausing]         = useState(false);
  const [debugJumping, setDebugJumping] = useState(false);
  const [forcedSpectator, setForcedSpectator] = useState(false);
  const [spectatorMode, setSpectatorMode]     = useState(false);
  const [widescreenMode, setWidescreenMode]   = useState(false);
  const spectatorModeRef = useRef(false);
  useEffect(() => { spectatorModeRef.current = spectatorMode; }, [spectatorMode]);

  // Server assigns 'scorer' to the first socket to join this match's room and 'spectator'
  // to every later joiner — routed through the hook's own single match:join subscription
  // (a second, separate useMatchSocket call for this same matchId would itself count as a
  // "later" join and self-trigger spectator mode). Always reflects the LATEST role rather
  // than latching on the first 'spectator' message forever — match:join can legitimately
  // fire more than once for the same socket (e.g. React StrictMode's dev-only double-invoke,
  // or a reconnect re-join), and a stale/transient 'spectator' from an earlier resolution
  // must not permanently lock out someone who the server has since confirmed as scorer.
  const onRoleAssigned = useCallback((role: 'scorer' | 'spectator') => {
    const isSpectator = role === 'spectator';
    setForcedSpectator(prevForced => {
      if (isSpectator) {
        // (Re-)forced into spectator — impose the view.
        setSpectatorMode(true);
        spectatorModeRef.current = true;
      } else if (prevForced) {
        // Self-healed from a forced-spectator state back to confirmed scorer — undo the
        // imposed view. If already confirmed scorer and this is just a redundant
        // re-confirmation (e.g. on reconnect), leave the user's own view choice alone.
        setSpectatorMode(false);
        spectatorModeRef.current = false;
      }
      return isSpectator;
    });
  }, []);

  const mp = useMatchPlay({
    matchId,
    apiBaseUrl: '',
    getAccessToken,
    audio,
    isStandardTournament: isStandard,
    t,
    useMatchSocket,
    useConnectionStatus,
    onRoleAssigned,
  });

  // Standard tournaments have no timing at all — mute clock sounds immediately.
  const isStandardRef = useRef(isStandard);
  useEffect(() => {
    isStandardRef.current = isStandard;
    if (isStandard) { setSoundsMuted(true); soundsMutedRef.current = true; }
  }, [isStandard]);

  const handlePauseAll = async () => {
    if (!matchTournament) return;
    setPausing(true);
    try {
      if (allPaused) {
        mp.unfreezeClock();
        await resumeAllClocks(matchTournament._id);
        setAllPaused(false);
      } else {
        mp.freezeClock();
        await pauseAllClocks(matchTournament._id);
        setAllPaused(true);
      }
    } finally { setPausing(false); }
  };

  const handleDebugJump = async () => {
    setDebugJumping(true);
    try {
      const elapsed330FromEnd = mp.matchDuration - 330;
      const token = await getAccessToken();
      await fetch(`/api/matches/${matchId}/debug-set-elapsed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ elapsedSeconds: elapsed330FromEnd }),
      });
    } finally { setDebugJumping(false); }
  };

  return (
    <MatchScoringSurface
      mp={mp}
      t={t}
      backLabel={t.backToBracket}
      onBack={onBack}
      isStandard={isStandard}
      spectatorFmt={tournamentFmt}
      spectatorMode={spectatorMode}
      setSpectatorMode={setSpectatorMode}
      widescreenMode={widescreenMode}
      setWidescreenMode={setWidescreenMode}
      forcedSpectator={forcedSpectator}
      soundsMuted={soundsMuted}
      onToggleMute={handleToggleMute}
      isSuperuser={isSuperuser}
      allPaused={allPaused}
      pausingAll={pausing}
      onPauseAll={handlePauseAll}
      debugJumping={debugJumping}
      onDebugJump={handleDebugJump}
      showRestartButton
      showMatchClockNote
    />
  );
}
